import { useState, useRef, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, useReducedMotion } from 'framer-motion';
import {
  ChevronDown,
  ArrowRight,
  Check,
  X,
  ShieldCheck,
  Banknote,
  Home as HomeIcon,
  Plane,
  Dog,
  Hotel,
  Languages,
  MapPin,
  FileCheck,
  TrendingUp,
  Lock,
  Camera,
  Calculator,
  ListChecks,
  BarChart3,
  Compass,
  BookOpen,
  Sparkles,
  Bookmark,
  Trees,
  Waves,
  ShoppingBag,
  Bus,
  UtensilsCrossed,
  Landmark,
} from 'lucide-react';

import { categories } from '@/data/categories';
import { PERSONAS, PERSONA_SLUGS } from '@/data/personas';
import { pathFor } from '@/data/routes';
import { usePersona } from '@/hooks/usePersona';
// Index dérivé de src/data/places.js (5,7 Ko au lieu de 300 Ko) — le Header
// est chargé sur toutes les pages, la liste des lieux ne peut pas y arriver
// avec ses 3 300 lignes de contenu éditorial. Régénéré au prebuild.
import { PLACES_NAV } from '@/generated/places-nav';
import { getLatestArticles } from '@/utils/articles';
import { useBookmarks } from '@/hooks/useBookmarks';

/**
 * Desktop mega-menu — drops down under each top-level item in the Header.
 * Shows category subitems, a featured article, and a quick-action chip
 * grid. Designed for editorial sites where flat horizontal nav buries
 * 60% of content. Mobile uses the existing MobileMenu drawer (untouched).
 *
 * Architecture :
 *   - Single hover/focus root <nav>. Each top-level button toggles a
 *     panel rendered just under the header bar (full-width).
 *   - One panel per top-level item (PANELS array below). Each panel
 *     has its own shape : grid of category cards, list of tools, etc.
 *   - Closing : Esc, click outside, or hover on another top-level item.
 *   - Accessibility : button[aria-expanded], panel[role=menu], Tab nav
 *     within panel, useReducedMotion respected.
 *
 * Why a custom mega-menu vs a library :
 *   - HeadlessUI's Menu is for dropdowns ≠ full-width panels
 *   - Radix Navigation Menu would work but adds 12 KB for one usage
 *   - This is ~150 lines + uses what we already have (framer-motion)
 *
 * @example
 *   <MegaMenu lang="fr" scrolled={scrolled} />
 */

const CATEGORY_ICONS = {
  'visa-residence': ShieldCheck,
  'banque-argent': Banknote,
  'immobilier': HomeIcon,
  'vols': Plane,
  'animaux-chiens': Dog,
  'assurance': Hotel,
  'espagnol': Languages,
  'vie-quotidienne': MapPin,
  'compliance': FileCheck,
  'investissement': TrendingUp,
  'securite': Lock,
};

/**
 * Copie du panneau « Mon profil » (cf. ProfilePanel en bas de fichier).
 * Déclarée ici parce que le composant racine s'en sert aussi, pour le
 * libellé de l'onglet et pour l'aria-label du panneau.
 *
 * Ce qui N'EST PAS ici : les libellés et accroches des 5 personas. Ils
 * sont lus dans PERSONAS (src/data/personas.js), seule source de vérité —
 * une seconde liste divergerait au premier persona ajouté ou renommé.
 *
 * `intro` énumère ce que le choix change RÉELLEMENT aujourd'hui : les
 * recommandations de fin d'article (StageCTA → PersonaCTA), la feuille de
 * route (PersonaRoadmap) et l'espace personnel (MonPanama). Si un de ces
 * trois branchements disparaît, cette phrase devient un mensonge.
 */
const PROFILE_COPY = {
  fr: {
    tab: 'Profil',
    panelLabel: 'Choisir ton profil',
    eyebrow: 'Ton profil',
    intro: 'Dis-nous qui tu es. On aligne ensuite les recommandations de fin d\'article, ta feuille de route et ton espace personnel.',
    pick: 'Choisir',
    active: 'Profil actif',
    change: 'Clique sur un autre profil pour réorienter le site.',
    roadmap: 'Voir ma feuille de route',
  },
  en: {
    tab: 'Profile',
    panelLabel: 'Choose your profile',
    eyebrow: 'Your profile',
    intro: 'Tell us who you are. We then line up the end-of-article recommendations, your roadmap and your personal space.',
    pick: 'Choose',
    active: 'Active profile',
    change: 'Click another profile to re-tune the site.',
    roadmap: 'See my roadmap',
  },
  es: {
    tab: 'Perfil',
    panelLabel: 'Elegir tu perfil',
    eyebrow: 'Tu perfil',
    intro: 'Dinos quién eres. Alineamos luego las recomendaciones de final de artículo, tu hoja de ruta y tu espacio personal.',
    pick: 'Elegir',
    active: 'Perfil activo',
    change: 'Haz clic en otro perfil para reorientar el sitio.',
    roadmap: 'Ver mi hoja de ruta',
  },
};

export default function MegaMenu({ lang = 'fr', scrolled = false }) {
  const { t } = useTranslation();
  const reduce = useReducedMotion();
  const location = useLocation();
  const [openId, setOpenId] = useState(null);
  const rootRef = useRef(null);

  // 2026-06 refonte : méga-menu désormais CLICK-to-open (fini le hover).
  // L'ouverture au survol rouvrait le panneau dès que la souris repassait
  // sur le nav → « impossible de l'enlever, impossible de naviguer »
  // (feedback Jade). Fermeture garantie : clic sur le trigger, X, Échap,
  // clic dehors, clic sur un lien, scroll, ou changement de route.
  const close = useCallback(() => {
    setOpenId(null);
  }, []);

  // Auto-close on route change. Jade audit 2026-05-15 : clic sur un
  // lien du panel naviguait mais le panel restait visible si la souris
  // restait au-dessus de la zone d'ouverture (onMouseEnter re-fire).
  // L'écoute de `location.pathname` ferme systématiquement et bypass
  // ce edge case. 2026-05-24 — ajout de search + hash : si l'URL change
  // par query/hash uniquement (cas rare mais existe sur /comparatifs),
  // sans cet ajout le menu reste collé.
  useEffect(() => {
    setOpenId(null);
  }, [location.pathname, location.search, location.hash]);

  // Tout clic dans le panel le ferme. Un clic sur un lien de navigation DOIT
  // refermer le menu — y compris un lien vers la page où l'on est déjà (la
  // route ne change pas, donc l'effet `location` ne se déclenche pas et ne
  // peut pas fermer à notre place). Le bouton X et les clics dans le vide
  // ferment aussi. On exclut seulement les champs de formulaire (aucun
  // aujourd'hui, mais robuste pour le futur).
  // Fix Jade 2026-06-14 : le menu restait ouvert après un clic sur un lien.
  const handlePanelClick = (e) => {
    if (e.target.closest('input, textarea, select')) return;
    close();
  };

  // Esc + click-outside + scroll → fermeture garantie en toutes circonstances
  useEffect(() => {
    if (openId === null) return;
    const onKey = (e) => {
      if (e.key === 'Escape') close();
    };
    const onClick = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) close();
    };
    const onScroll = () => close();
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onClick);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onClick);
      window.removeEventListener('scroll', onScroll);
    };
  }, [openId, close]);

  const profileCopy = PROFILE_COPY[lang] ?? PROFILE_COPY.fr;

  // Top-level items. `panel: null` means a regular link (no dropdown).
  const topLevel = [
    { id: 'blog', label: t('navigation:blogShort'), route: 'blog', panel: 'blog' },
    { id: 'tools', label: t('navigation:tools'), route: 'tools', panel: 'tools' },
    { id: 'comparisons', label: t('navigation:comparisons'), route: 'comparisons', panel: 'comparisons' },
    { id: 'discover', label: lang === 'en' ? 'Discover' : lang === 'es' ? 'Descubrir' : 'Découvrir', route: 'history', panel: 'discover' },
    // Visible à partir de xl (1280) seulement. MESURÉ à 1024 px : la barre
    // dispose de 945 px de contenu ; en espagnol les cinq onglets existants +
    // le logo + les contrôles de droite en réclament déjà 956. Un sixième
    // onglet y sortirait le sélecteur de langue de l'écran. Le profil reste
    // atteignable ailleurs (/commencer-ici, routeur de la home, /ma-roadmap).
    { id: 'profile', label: profileCopy.tab, route: 'myRoadmap', panel: 'profile', wrapperClass: 'hidden xl:block' },
    { id: 'shop', label: t('navigation:shop'), route: 'shop', panel: null },
  ];

  return (
    <nav
      ref={rootRef}
      className="hidden lg:flex items-center gap-1"
      aria-label={t('navigation:primary')}
    >
      {topLevel.map((item) => {
        if (!item.panel) {
          return (
            <Link
              key={item.id}
              to={pathFor(item.route, lang)}
              // px-2 sous 2xl. MESURÉ, en espagnol (la langue aux libellés les
              // plus longs) : à 1024 px la barre n'offre que 945 px de contenu
              // et en réclamait 956 — le sélecteur de langue sortait déjà de
              // l'écran, avant même l'onglet Profil. À 1280 px avec le badge
              // persona le plus long (« Emprendedor »), 1 215 px demandés pour
              // 1 201 px. 8 px × 6 onglets remettent les deux cas au vert.
              className={`rounded-btn px-2 py-2 text-sm font-medium transition-colors 2xl:px-3 ${
                scrolled ? 'text-cream-50 hover:bg-navy-700' : 'text-navy-900 hover:bg-cream-100 dark:text-white dark:hover:bg-white/10'
              }`}
            >
              {item.label}
            </Link>
          );
        }
        const isOpen = openId === item.id;
        return (
          <div
            key={item.id}
            className={`relative ${item.wrapperClass ?? ''}`}
          >
            <button
              type="button"
              aria-expanded={isOpen}
              aria-haspopup="true"
              onClick={() => {
                if (isOpen) {
                  close();
                } else {
                  setOpenId(item.id);
                }
              }}
              // px-2 sous 2xl — même raison que le lien ci-dessus.
              className={`inline-flex items-center gap-1 rounded-btn px-2 py-2 text-sm font-medium transition-colors 2xl:px-3 ${
                scrolled ? 'text-cream-50 hover:bg-navy-700' : 'text-navy-900 hover:bg-cream-100 dark:text-white dark:hover:bg-white/10'
              } ${isOpen ? (scrolled ? 'bg-navy-700' : 'bg-cream-100 dark:bg-white/10') : ''}`}
            >
              {item.label}
              <ChevronDown
                size={12}
                aria-hidden="true"
                className={`transition-transform ${isOpen ? 'rotate-180' : ''}`}
              />
            </button>
          </div>
        );
      })}

      {/* Full-width dropdown panel — rendered once, switched by openId.
          Wrapped in a fixed backdrop that closes the panel on any click
          outside the visible content area (fix Jade 2026-05-24). */}
      {/* PAS d'AnimatePresence ici. Sous React StrictMode (dev) — et par
          intermittence en prod — AnimatePresence ne démontait PAS le panneau
          quand `openId` repassait à null SANS changement de route : le nœud
          restait « collé » dans le DOM plusieurs secondes après fermeture
          (état React pourtant fermé, aria-expanded=false). On rend donc le
          panneau en conditionnel pur → React le démonte instantanément et de
          façon garantie à chaque fermeture. L'animation d'ENTRÉE reste
          (initial→animate) ; la fermeture est instantanée, ce qui est le
          comportement attendu d'un menu de navigation. Fix Jade 2026-06-14. */}
      {openId && (
          <motion.div
            key="megamenu-backdrop"
            initial={reduce ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.15 }}
            onClick={close}
            className="fixed inset-0 top-20 z-20 bg-transparent"
            aria-hidden="true"
          />
        )}
        {openId && (
          <motion.div
            key={openId}
            initial={reduce ? false : { opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
            // a11y audit (2026-05-13) : `role="menu"` exigerait des enfants
            // `role="menuitem"` + keyboard arrow nav. Notre panneau contient
            // des <Link> standards → on retire le rôle pour laisser le SR
            // annoncer une simple région de navigation.
            aria-label={openId === 'blog' ? 'Catégories du blog'
              : openId === 'tools' ? 'Outils gratuits'
              : openId === 'comparisons' ? 'Comparatifs'
              // Seul libellé traduit de la chaîne : les quatre autres sont en
              // français quelle que soit la langue (dette antérieure, hors
              // périmètre). Autant ne pas en ajouter une cinquième.
              : openId === 'profile' ? profileCopy.panelLabel
              : 'Découvrir le Panama'}
            className="fixed left-0 right-0 top-20 z-30 border-t border-navy-900/10 bg-cream-50 shadow-card-hover dark:border-white/10 dark:bg-zinc-950 dark:shadow-none"
            onClick={handlePanelClick}
          >
            <div className="relative mx-auto max-w-content px-6 py-8">
              {/* Close button — top-right of the panel. Ajouté 2026-05-16
                  après feedback Jade : le menu était dur à fermer pour
                  les users qui hésitent avec la souris. Bouton X = sortie
                  garantie en 1 clic + visible visuellement. */}
              <button
                type="button"
                onClick={close}
                aria-label={t('navigation:closeMenu', { defaultValue: 'Fermer le menu' })}
                className="absolute right-4 top-4 inline-flex h-9 w-9 items-center justify-center rounded-btn text-gray-700 transition-colors hover:bg-cream-100 hover:text-navy-900 dark:text-white/60 dark:hover:bg-white/10 dark:hover:text-white"
              >
                <X size={18} aria-hidden="true" />
              </button>
              {openId === 'blog' && <BlogPanel lang={lang} onNavigate={close} />}
              {openId === 'tools' && <ToolsPanel lang={lang} onNavigate={close} />}
              {openId === 'comparisons' && <ComparisonsPanel lang={lang} onNavigate={close} />}
              {openId === 'discover' && <DiscoverPanel lang={lang} onNavigate={close} />}
              {openId === 'profile' && <ProfilePanel lang={lang} onNavigate={close} />}
            </div>
          </motion.div>
        )}
    </nav>
  );
}

// ============================================================
// PANEL : Blog → categories + latest article preview
// ============================================================
function BlogPanel({ lang, onNavigate }) {
  const featured = getLatestArticles({ lang, limit: 1 })[0]?.frontmatter;
  return (
    <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1.5fr_1fr]">
      {/* Left : category grid */}
      <div>
        {/* V4 — eyebrow avec ::before hairline (28px). Garde la couleur
            bordeaux brand pour cohérence avec le reste du site. */}
        <p className="v4-eyebrow mb-3 text-bordeaux-700 dark:text-bordeaux-400">
          {lang === 'en' ? 'Explore by topic' : lang === 'es' ? 'Explorar por tema' : 'Explorer par sujet'}
        </p>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
          {categories.map((cat) => {
            const Icon = CATEGORY_ICONS[cat.slug] || MapPin;
            return (
              <Link
                key={cat.slug}
                to={pathFor('category', lang, { slug: cat.slug })}
                onClick={onNavigate}
                className="group flex items-start gap-3 rounded-card p-3 transition-colors hover:bg-cream-100 dark:hover:bg-white/5"
              >
                <span className="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-bordeaux-600/10 text-bordeaux-700 transition-colors group-hover:bg-bordeaux-600 group-hover:text-cream-50 dark:bg-bordeaux-600/20 dark:text-bordeaux-400">
                  <Icon size={16} aria-hidden="true" />
                </span>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-navy-900 dark:text-white">{cat.label[lang] ?? cat.label.fr}</p>
                  <p className="mt-0.5 line-clamp-2 text-xs text-gray-700 dark:text-white/60">{cat.description[lang] ?? cat.description.fr}</p>
                </div>
              </Link>
            );
          })}
        </div>
        <div className="mt-5">
          <Link
            to={pathFor('blog', lang)}
            onClick={onNavigate}
            className="inline-flex items-center gap-1 text-sm font-medium text-bordeaux-700 hover:text-bordeaux-700 dark:text-bordeaux-300 dark:hover:text-bordeaux-200"
          >
            {lang === 'en' ? 'See all articles' : lang === 'es' ? 'Ver todos' : 'Voir tous les articles'}
            <ArrowRight size={13} aria-hidden="true" />
          </Link>
        </div>
      </div>

      {/* Right : featured article */}
      {featured && (
        <Link
          to={pathFor('article', lang, { category: featured.category, slug: featured.slug })}
          onClick={onNavigate}
          className="group flex flex-col overflow-hidden rounded-card border border-navy-900/10 bg-cream-50 shadow-card transition-all hover:shadow-card-hover dark:border-white/10 dark:bg-zinc-900 dark:shadow-none"
        >
          {featured.featuredImage && (
            <div className="aspect-[16/9] w-full overflow-hidden bg-navy-900/5 dark:bg-white/5">
              <img
                src={featured.featuredImage}
                alt=""
                loading="lazy"
                className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
            </div>
          )}
          <div className="flex-1 p-4">
            {/* V4 — "À la une" label mono editorial pour le tag featured card */}
            <p className="v4-label-mono text-bordeaux-700 dark:text-bordeaux-400">
              {lang === 'en' ? 'Featured' : lang === 'es' ? 'Destacado' : 'À la une'}
            </p>
            <p className="mt-1.5 font-serif text-base font-semibold leading-tight text-navy-900 group-hover:text-bordeaux-700 dark:text-white dark:group-hover:text-bordeaux-300">
              {featured.title}
            </p>
            {featured.excerpt && (
              <p className="mt-2 line-clamp-2 text-xs leading-relaxed text-gray-700 dark:text-white/60">{featured.excerpt}</p>
            )}
          </div>
        </Link>
      )}
    </div>
  );
}

// ============================================================
// PANEL : Tools → list of free tools + checkboxes/calc
// ============================================================
function ToolsPanel({ lang, onNavigate }) {
  const tools = [
    { Icon: Calculator, key: 'budget',
      label: { fr: 'Calculatrice budget', en: 'Budget calculator', es: 'Calculadora de presupuesto' },
      desc: { fr: 'Coût de la vie réaliste par profil', en: 'Realistic cost of living by profile', es: 'Costo de vida por perfil' },
      href: 'tools' },
    { Icon: ListChecks, key: 'visa',
      label: { fr: 'Checklist visa Friendly Nations', en: 'Friendly Nations checklist', es: 'Checklist visa Friendly Nations' },
      desc: { fr: '47 documents pas-à-pas', en: '47 documents step-by-step', es: '47 documentos paso a paso' },
      href: 'tools' },
    { Icon: Dog, key: 'dog',
      label: { fr: 'Checklist chien', en: 'Dog import checklist', es: 'Checklist perro' },
      desc: { fr: 'Vaccins, MIDA, IATA', en: 'Vaccines, MIDA, IATA', es: 'Vacunas, MIDA, IATA' },
      href: 'tools' },
    { Icon: BarChart3, key: 'mortgage',
      label: { fr: 'Simulateur hypothèque', en: 'Mortgage simulator', es: 'Simulador hipoteca' },
      desc: { fr: 'Taux résident vs non-résident', en: 'Resident vs non-resident rates', es: 'Tasas residente vs no-residente' },
      href: 'tools' },
    { Icon: Calculator, key: 'tax',
      label: { fr: 'Simulateur fiscal', en: 'Tax simulator', es: 'Simulador fiscal' },
      desc: { fr: 'Système territorial expliqué', en: 'Territorial system explained', es: 'Sistema territorial' },
      href: 'tools' },
    { Icon: MapPin, key: 'neighborhood',
      label: { fr: 'Quiz quartier', en: 'Neighborhood quiz', es: 'Quiz de barrio' },
      desc: { fr: '5 questions → ton quartier idéal', en: '5 questions → your ideal area', es: '5 preguntas → tu barrio ideal' },
      href: 'tools' },
  ];
  return (
    <div>
      <p className="v4-eyebrow mb-3 text-bordeaux-700 dark:text-bordeaux-400">
        {lang === 'en' ? 'Free tools' : lang === 'es' ? 'Herramientas gratis' : 'Outils gratuits'}
      </p>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
        {tools.map((tool) => {
          const Icon = tool.Icon;
          return (
            <Link
              key={tool.key}
              to={pathFor(tool.href, lang)}
              onClick={onNavigate}
              className="group flex items-start gap-3 rounded-card p-3 transition-colors hover:bg-cream-100 dark:hover:bg-white/5"
            >
              <span className="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-bordeaux-600/10 text-bordeaux-700 transition-colors group-hover:bg-bordeaux-700 group-hover:text-cream-50 dark:bg-bordeaux-600/20 dark:text-bordeaux-300">
                <Icon size={16} aria-hidden="true" />
              </span>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-navy-900 dark:text-white">{tool.label[lang] ?? tool.label.fr}</p>
                <p className="mt-0.5 text-xs text-gray-700 dark:text-white/60">{tool.desc[lang] ?? tool.desc.fr}</p>
              </div>
            </Link>
          );
        })}
      </div>
      <div className="mt-5">
        <Link
          to={pathFor('tools', lang)}
          onClick={onNavigate}
          className="inline-flex items-center gap-1 text-sm font-medium text-bordeaux-700 hover:text-bordeaux-700 dark:text-bordeaux-300 dark:hover:text-bordeaux-200"
        >
          {lang === 'en' ? 'See all tools' : lang === 'es' ? 'Ver todas' : 'Voir tous les outils'}
          <ArrowRight size={13} aria-hidden="true" />
        </Link>
      </div>
    </div>
  );
}

// ============================================================
// PANEL : Comparisons → list of comparison types
// ============================================================
function ComparisonsPanel({ lang, onNavigate }) {
  const items = [
    { key: 'banques', label: { fr: 'Banques', en: 'Banks', es: 'Bancos' }, slug: 'banques-panama' },
    { key: 'assurances', label: { fr: 'Assurances santé', en: 'Health insurance', es: 'Seguros salud' }, slug: 'assurances-sante-panama' },
    { key: 'hopitaux', label: { fr: 'Hôpitaux', en: 'Hospitals', es: 'Hospitales' }, slug: 'hopitaux-panama-city' },
    { key: 'ecoles', label: { fr: 'Écoles internationales', en: 'International schools', es: 'Colegios internacionales' }, slug: 'ecoles-internationales' },
    { key: 'coworkings', label: { fr: 'Coworkings', en: 'Coworking', es: 'Coworking' }, slug: 'co-working-panama-city' },
    { key: 'visa-types', label: { fr: 'Types de visa', en: 'Visa types', es: 'Tipos de visa' }, slug: 'visas-residence-panama' },
  ];
  return (
    <div>
      <p className="v4-eyebrow mb-3 text-bordeaux-700 dark:text-bordeaux-400">
        {lang === 'en' ? 'Side-by-side comparisons' : lang === 'es' ? 'Comparativas lado a lado' : 'Comparatifs côte-à-côte'}
      </p>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
        {items.map((it) => (
          <Link
            key={it.key}
            to={pathFor('comparison', lang, { slug: it.slug })}
            onClick={onNavigate}
            className="group flex items-center gap-3 rounded-card p-3 transition-colors hover:bg-cream-100 dark:hover:bg-white/5"
          >
            <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gold-500/15 text-gold-700 dark:bg-gold-500/20 dark:text-gold-400">
              <BarChart3 size={16} aria-hidden="true" />
            </span>
            <p className="text-sm font-semibold text-navy-900 dark:text-white">{it.label[lang] ?? it.label.fr}</p>
          </Link>
        ))}
      </div>
      <div className="mt-5">
        <Link
          to={pathFor('comparisons', lang)}
          onClick={onNavigate}
          className="inline-flex items-center gap-1 text-sm font-medium text-bordeaux-700 hover:text-bordeaux-700 dark:text-bordeaux-300 dark:hover:text-bordeaux-200"
        >
          {lang === 'en' ? 'See all comparisons' : lang === 'es' ? 'Ver todas' : 'Voir tous les comparatifs'}
          <ArrowRight size={13} aria-hidden="true" />
        </Link>
      </div>
    </div>
  );
}

// ============================================================
// PANEL : Discover → colonnes de catégories + raccourcis perso
// ============================================================

/**
 * Icône déduite du type Schema.org de la fiche. L'ordre départage les lieux
 * qui en portent plusieurs (une plage-parc s'affiche en plage).
 */
const PLACE_TYPE_ICONS = [
  ['Beach', Waves],
  ['Park', Trees],
  ['Neighborhood', HomeIcon],
  ['ShoppingCenter', ShoppingBag],
  ['BusStation', Bus],
  ['FoodEstablishment', UtensilsCrossed],
  ['EntertainmentBusiness', Sparkles],
  ['HistoricalLandmark', Landmark],
  ['LandmarksOrHistoricalBuildings', Landmark],
];

function placeIcon(place) {
  for (const [type, Icon] of PLACE_TYPE_ICONS) {
    if (place.types.includes(type)) return Icon;
  }
  return MapPin;
}

const inCity = (place) => place.region === 'Panamá';
const hasType = (place, ...types) => types.some((t) => place.types.includes(t));

/**
 * Colonnes de lieux. L'appartenance se DÉDUIT de la fiche (`types`
 * Schema.org + région), au lieu d'une liste de lieux recopiée dans le menu :
 * cette recopie avait déjà divergé de sa source — 17 lieux affichés pour 21
 * fiches en ligne, dont 4 pages publiées et injoignables depuis la nav.
 *
 * L'ORDRE COMPTE : un lieu rejoint la PREMIÈRE colonne qui le reconnaît.
 * `TouristAttraction` est volontairement absent de toutes les règles — il
 * porte sur 18 fiches sur 21, donc il ne trie rien.
 *
 * La dernière colonne est un filet : une fiche future sans `types`
 * exploitable reste visible au lieu de disparaître en silence.
 */
const PLACE_COLUMNS = [
  {
    id: 'nature',
    title: { fr: 'Parcs & nature', en: 'Parks & nature', es: 'Parques y naturaleza' },
    match: (p) => inCity(p) && hasType(p, 'Park', 'Beach'),
  },
  {
    id: 'patrimoine',
    title: { fr: 'Patrimoine', en: 'Heritage', es: 'Patrimonio' },
    match: (p) => inCity(p) && hasType(p, 'HistoricalLandmark', 'LandmarksOrHistoricalBuildings'),
  },
  {
    id: 'ville',
    title: { fr: 'En ville', en: 'In the city', es: 'En la ciudad' },
    match: (p) => inCity(p) && hasType(p, 'Neighborhood', 'ShoppingCenter', 'BusStation', 'FoodEstablishment', 'EntertainmentBusiness'),
  },
  {
    id: 'hors-ville',
    title: { fr: 'Hors de la ville', en: 'Outside the city', es: 'Fuera de la ciudad' },
    match: (p) => !inCity(p),
  },
  {
    id: 'autres',
    title: { fr: 'Autres lieux', en: 'Other places', es: 'Otros lugares' },
    match: () => true,
  },
];

const COLUMN_TITLE_CLASS = 'mb-2 block text-eyebrow uppercase text-bordeaux-700 dark:text-bordeaux-400';
const NAV_LINK_CLASS =
  'group flex items-start gap-2 rounded-btn px-2 py-1.5 text-sm text-navy-900 transition-colors hover:bg-cream-100 dark:text-white dark:hover:bg-white/5';
const NAV_ICON_CLASS = 'mt-1 shrink-0 text-bordeaux-700 dark:text-bordeaux-400';

function DiscoverPanel({ lang, onNavigate }) {
  // Read bookmarks here so the count badge in the panel stays live as users
  // bookmark/un-bookmark articles in other tabs (the hook listens to the
  // `storage` event for cross-tab sync).
  const { items: bookmarkItems } = useBookmarks();
  const bookmarkCount = bookmarkItems.length;

  // Répartition en colonnes — premier match gagnant, d'où l'ordre du tableau.
  const columns = PLACE_COLUMNS.map((col) => ({ ...col, places: [] }));
  for (const place of PLACES_NAV) {
    columns.find((col) => col.match(place))?.places.push(place);
  }

  // Dernière colonne : ce qui n'est pas un lieu. Raccourcis vers l'espace
  // personnel et les pages transverses.
  const shortcuts = [
    {
      Icon: Sparkles,
      route: 'monPanama',
      // Highlighted entry — pulls the user toward their personal dashboard,
      // which holds streak, badges, continue-reading, persona quiz result.
      label: { fr: 'Mon Panama', en: 'My Panama', es: 'Mi Panamá' },
    },
    {
      Icon: ListChecks,
      route: 'startHere',
      label: { fr: 'Commencer ici', en: 'Start here', es: 'Empezar aquí' },
    },
    {
      Icon: BookOpen,
      route: 'history',
      label: { fr: 'Histoire du Panama', en: 'Panama history', es: 'Historia de Panamá' },
    },
    {
      Icon: Compass,
      route: 'map',
      label: { fr: 'Carte interactive', en: 'Interactive map', es: 'Mapa interactivo' },
    },
    {
      Icon: Camera,
      route: 'gallery',
      label: { fr: 'Galerie photo', en: 'Gallery', es: 'Galería' },
    },
    {
      Icon: Plane,
      route: 'flights',
      label: { fr: 'Vols Panama', en: 'Flights to Panama', es: 'Vuelos a Panamá' },
    },
    {
      Icon: Languages,
      route: 'remoteWork',
      label: { fr: 'Travail à distance', en: 'Remote work', es: 'Teletrabajo' },
    },
    {
      Icon: Bookmark,
      route: 'bookmarks',
      label: { fr: 'Mes favoris', en: 'My bookmarks', es: 'Mis favoritos' },
      // Live count from useBookmarks hook (cross-tab synced via `storage`)
      count: bookmarkCount,
    },
  ];

  return (
    <div>
      {/* Le titre du panneau garde la bande du haut pour lui seul : le bouton
          X est en absolute top-4 right-4, une colonne qui démarrerait là se
          ferait recouvrir. */}
      <p className="v4-eyebrow mb-5 text-bordeaux-700 dark:text-bordeaux-400">
        {lang === 'en' ? 'Discover Panama' : lang === 'es' ? 'Descubrir Panamá' : 'Découvrir le Panama'}
      </p>
      <div className="grid grid-cols-2 gap-x-6 gap-y-8 lg:grid-cols-3 xl:grid-cols-5">
        {columns.map((col) => {
          // Une colonne sans lieu ne se rend pas : le menu suit les données
          // au lieu d'afficher un intitulé vide.
          if (col.places.length === 0) return null;
          return (
            <section key={col.id} aria-labelledby={`discover-col-${col.id}`}>
              <p id={`discover-col-${col.id}`} className={COLUMN_TITLE_CLASS}>
                {col.title[lang] ?? col.title.fr}
              </p>
              <ul>
                {col.places.map((place) => {
                  const Icon = placeIcon(place);
                  return (
                    <li key={place.slug}>
                      <Link
                        to={pathFor('place', lang, { slug: place.slug })}
                        onClick={onNavigate}
                        className={NAV_LINK_CLASS}
                      >
                        <Icon size={14} aria-hidden="true" className={NAV_ICON_CLASS} />
                        <span className="min-w-0">{place.title[lang] ?? place.title.fr}</span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </section>
          );
        })}
        <section aria-labelledby="discover-col-perso">
          <p id="discover-col-perso" className={COLUMN_TITLE_CLASS}>
            {lang === 'en' ? 'Your Panama' : lang === 'es' ? 'Tu Panamá' : 'Ton Panama'}
          </p>
          <ul>
            {shortcuts.map((it) => {
              const Icon = it.Icon;
              return (
                <li key={it.route}>
                  <Link to={pathFor(it.route, lang)} onClick={onNavigate} className={NAV_LINK_CLASS}>
                    <Icon size={14} aria-hidden="true" className={NAV_ICON_CLASS} />
                    <span className="min-w-0">
                      {it.label[lang] ?? it.label.fr}
                      {typeof it.count === 'number' && it.count > 0 && (
                        <span
                          className="ml-2 inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-gold-500 px-1.5 py-0.5 text-[10px] font-semibold text-cream-50"
                          aria-label={`${it.count} ${lang === 'en' ? 'saved' : lang === 'es' ? 'guardados' : 'enregistrés'}`}
                        >
                          {it.count}
                        </span>
                      )}
                    </span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </section>
      </div>
    </div>
  );
}

// ============================================================
// PANEL : Mon profil → les 5 personas, un clic oriente le site
// ============================================================

/**
 * ProfilePanel — le visiteur se désigne, le site s'aligne.
 *
 * Demande Jade (2026-07-22) : « une liste de choix pour guider
 * l'utilisateur, il choisit son profil, et après on le guide comme le
 * reste du site mais filtré sur sa catégorie. »
 *
 * Un clic fait DEUX choses indissociables : il écrit le persona dans le
 * contexte (donc dans localStorage, donc sur toutes les pages) ET il
 * emmène sur /ma-roadmap, qui rend la feuille de route de CE profil.
 * D'où le <Link> plutôt qu'un <button> : la destination est une vraie
 * page, le clavier, le focus et l'ouverture en nouvel onglet marchent
 * sans code à nous.
 *
 * Pas de badge « profil actif » dans la barre de nav : PersonaBadge
 * (Header, ≥ md) tient déjà ce rôle. Un second marqueur dirait la même
 * chose deux fois.
 */
function ProfilePanel({ lang, onNavigate }) {
  const { personaSlug, setPersona } = usePersona();
  const c = PROFILE_COPY[lang] ?? PROFILE_COPY.fr;

  return (
    <div>
      {/* pr-12 : le bouton X du panneau est en absolute top-4 right-4 —
          sans cette réserve, l'intro passe dessous sur écran étroit. */}
      <div className="mb-5 max-w-3xl pr-12">
        <p className="v4-eyebrow mb-2 text-bordeaux-700 dark:text-bordeaux-400">{c.eyebrow}</p>
        {/* cream-50 et pas `white` en sombre : la charte V6 bannit le blanc
            pur. Les quatre panneaux voisins écrivent encore `dark:text-white`
            (dette antérieure) — on ne la propage pas dans du code neuf. */}
        <p className="text-sm leading-relaxed text-gray-700 dark:text-cream-50/60">{c.intro}</p>
      </div>

      <ul className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5">
        {PERSONA_SLUGS.map((slug) => {
          const p = PERSONAS[slug];
          const isActive = slug === personaSlug;
          return (
            <li key={slug}>
              <Link
                to={pathFor('myRoadmap', lang)}
                onClick={() => { setPersona(slug); onNavigate(); }}
                aria-current={isActive ? 'true' : undefined}
                className={`group flex h-full flex-col gap-2 rounded-card border p-3 transition-colors ${
                  isActive
                    ? 'border-gold-500 bg-cream-100 dark:border-gold-400 dark:bg-cream-50/10'
                    : 'border-navy-900/10 bg-cream-50 hover:border-navy-900/25 hover:bg-cream-100 dark:border-cream-50/10 dark:bg-zinc-900 dark:hover:border-cream-50/25'
                }`}
              >
                <span className="flex items-center gap-2.5">
                  {/* L'emoji est le marqueur d'identité du persona (porté par
                      la donnée, pas une décoration) — même traitement que
                      StartHerePersonaPicker : cerclé, teinté de l'accent,
                      masqué aux lecteurs d'écran puisque le libellé suit.
                      Le CERCLE, lui, reste neutre : l'accent d'`entrepreneur`
                      est l'encre #23201A, invisible en mode sombre (vérifié
                      sur les valeurs calculées). Un contour qui disparaît pour
                      un persona sur cinq n'est pas un contour. */}
                  <span
                    className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-navy-900/15 text-base dark:border-cream-50/20"
                    style={{ backgroundColor: `${p.accentHex}1A` }}
                    aria-hidden="true"
                  >
                    {p.emoji}
                  </span>
                  <span className="min-w-0 text-sm font-semibold text-navy-900 dark:text-cream-50">
                    {p.label[lang] ?? p.label.fr}
                  </span>
                </span>
                <span className="text-xs leading-relaxed text-gray-700 dark:text-cream-50/60">
                  {p.startHereTeaser[lang] ?? p.startHereTeaser.fr}
                </span>
                {/* mt-auto : la ligne d'action s'aligne en bas de toutes les
                    cartes, quelle que soit la longueur de l'accroche. */}
                <span
                  className={`mt-auto inline-flex items-center gap-1 pt-1 text-xs font-semibold ${
                    isActive
                      ? 'text-gold-700 dark:text-gold-400'
                      : 'text-bordeaux-700 dark:text-bordeaux-300'
                  }`}
                >
                  {isActive ? (
                    <>
                      <Check size={12} aria-hidden="true" />
                      {c.active}
                    </>
                  ) : (
                    <>
                      {c.pick}
                      <ArrowRight size={12} aria-hidden="true" className="transition-transform group-hover:translate-x-0.5" />
                    </>
                  )}
                </span>
              </Link>
            </li>
          );
        })}
      </ul>

      <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2">
        <Link
          to={pathFor('myRoadmap', lang)}
          onClick={onNavigate}
          className="inline-flex items-center gap-1 text-sm font-medium text-bordeaux-700 hover:text-bordeaux-700 dark:text-bordeaux-300 dark:hover:text-bordeaux-200"
        >
          {c.roadmap}
          <ArrowRight size={13} aria-hidden="true" />
        </Link>
        {personaSlug && (
          <p className="text-xs italic text-gray-700 dark:text-cream-50/60">{c.change}</p>
        )}
      </div>
    </div>
  );
}
